import type { OpsHeartbeat } from '@console/types'
import { formatAgeMs } from '../format.js'

/**
 * OPS-013. The box's healthcheck runs every 5 minutes; three missed runs is
 * when "no open problems" stops meaning anything. Past this, the screen says
 * the list may be out of date rather than presenting it as all-clear.
 */
export const HEARTBEAT_STALE_MS = 15 * 60_000

export type HeartbeatStatus = 'never' | 'stale' | 'fresh'

export function heartbeatStatus(heartbeat: OpsHeartbeat | null, nowMs: number): HeartbeatStatus {
  if (heartbeat === null) return 'never'
  return nowMs - Date.parse(heartbeat.lastRunAt) > HEARTBEAT_STALE_MS ? 'stale' : 'fresh'
}

/**
 * Keys are the ones `pruvato-healthcheck.sh` reports. An unknown key falls
 * back to the raw key: the box's own word beats a guessed label, and a new
 * check still shows up before this list learns about it.
 */
const OPS_CHECK_LABEL: Record<string, string> = {
  postfix_down: 'Inbound mail server down',
  disk_high: 'Disk nearly full',
  queue_backed_up: 'Mail queue backed up',
  queue_stuck: 'Mail stuck in the queue',
  queue_unreadable: 'Mail queue unreadable',
  backup_missing: 'No database backups found',
  backup_stale: 'Database backup overdue',
  deadletter_pending: 'Inbound emails awaiting review',
  // From pruvato-abuse-check.sh: signals that could get the server blacklisted or suspended again.
  ip_blacklisted: 'Server IP on a spam blacklist',
  blacklist_check_failed: 'Blacklist status unknown',
  outbound_smtp_blocked: 'Server tried to send email directly',
  outbound_smtp_connection: 'Server connected to an outside mail server',
  mail_sent_externally: 'Mail delivered outside the server',
  mail_discarded_externally: 'Outbound email generated (blocked)',
  bounce_generated: 'Bounce email generated',
  relay_safeguard_missing: 'Anti-spam safeguard missing',
  unexpected_listener: 'Unexpected open port',
  abuse_check_failed: 'Abuse check not running',
}

export function opsCheckLabel(checkKey: string): string {
  return OPS_CHECK_LABEL[checkKey] ?? checkKey
}

/** "5 min ago", but "just now" rather than "just now ago". */
export function formatAgo(ms: number): string {
  const age = formatAgeMs(ms)
  return age === 'just now' ? age : `${age} ago`
}
